/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import isEmpty from 'lodash/isEmpty';

import { RESOURCE_TYPES } from '../constants/constants';

import * as DataItemAPI from 'DataItemAPI';

export const GET_DATAITEMS = 'GET_DATAITEMS';
export const GET_DATAITEMS_FAILURE = 'GET_DATAITEMS_FAILURE';
export const GET_DATAITEMS_SUCCESS = 'GET_DATAITEMS_SUCCESS';
export const CLEAR_DATAITEMS = 'CLEAR_DATAITEMS';

export const GET_FILTERED_DATAITEMS = 'GET_FILTERED_DATAITEMS';
export const GET_FILTERED_DATAITEMS_FAILURE = 'GET_FILTERED_DATAITEMS_FAILURE';
export const GET_FILTERED_DATAITEMS_SUCCESS = 'GET_FILTERED_DATAITEMS_SUCCESS';

export const GET_DATAITEM_DETAILS = 'GET_DATAITEM_DETAILS';
export const GET_DATAITEM_DETAILS_FAILURE = 'GET_DATAITEM_DETAILS_FAILURE';
export const GET_DATAITEM_DETAILS_SUCCESS = 'GET_DATAITEM_DETAILS_SUCCESS';

export const CLEAR_DATAITEM_DETAILS = 'CLEAR_DATAITEM_DETAILS';

export const GET_DATAITEM_GRAPH = 'GET_DATAITEM_GRAPH';
export const GET_DATAITEM_GRAPH_SUCCESS = 'GET_DATAITEM_GRAPH_SUCCESS';
export const GET_DATAITEM_GRAPH_FAILURE = 'GET_DATAITEM_GRAPH_FAILURE';

export const GET_DATAITEM_RECENTDATA = 'GET_DATAITEM_RECENTDATA';
export const GET_DATAITEM_RECENTDATA_SUCCESS = 'GET_DATAITEM_RECENTDATA_SUCCESS';
export const GET_DATAITEM_RECENTDATA_FAILURE = 'GET_DATAITEM_RECENTDATA_FAILURE';

// ---------------
// EntityType Data Elements Actions
// ---------------

const getDataItemsRequest = () => ({
  type: GET_DATAITEMS,
});

const getDataItemsFailure = error => ({
  type: GET_DATAITEMS_FAILURE,
  payload: error,
});

export const clearDataItems = () => ({
  type: CLEAR_DATAITEMS,
});

const getDataItemsSuccess = (id, dataItems) => ({
  type: GET_DATAITEMS_SUCCESS,
  id,
  payload: dataItems,
});

export const fetchDataItems = hierarchyPath => dispatch => {
  dispatch(getDataItemsRequest());
  return DataItemAPI.getDataItemsAPI(hierarchyPath)
    .then(responseJson => {
      dispatch(getDataItemsSuccess(hierarchyPath, responseJson.data || ''));
      return responseJson.data;
    })
    .catch(error => {
      dispatch(getDataItemsFailure(error));
    });
};

const getHierarchyDataItemsRequest = () => ({
  type: GET_FILTERED_DATAITEMS,
});

const getHierarchyDataItemsFailure = error => ({
  type: GET_FILTERED_DATAITEMS_FAILURE,
  payload: error,
});

const getHierarchyDataItemsSuccess = (id, hierarchyDataItems) => ({
  type: GET_FILTERED_DATAITEMS_SUCCESS,
  id,
  payload: hierarchyDataItems,
});

export const fetchHierarchyDataItems = hierarchyPath => dispatch => {
  dispatch(getHierarchyDataItemsRequest());
  return DataItemAPI.getDataItemsAPI(hierarchyPath, true)
    .then(responseJson => {
      dispatch(getHierarchyDataItemsSuccess(hierarchyPath, responseJson.data || ''));
      return responseJson.data;
    })
    .catch(error => {
      dispatch(getHierarchyDataItemsFailure(error));
    });
};

// ---------------
// DataItem Details Actions
// ---------------

const getDataItemDetailsRequest = () => ({
  type: GET_DATAITEM_DETAILS,
});

const getDataItemDetailsFailure = error => ({
  type: GET_DATAITEM_DETAILS_FAILURE,
  payload: error,
});

const getDataItemDetailsSuccess = dataItems => ({
  type: GET_DATAITEM_DETAILS_SUCCESS,
  payload: dataItems,
});

export const clearDataItemDetails = () => ({
  type: CLEAR_DATAITEM_DETAILS,
});

export const fetchDataItemDetails = (hierarchyPath, dataItem) => dispatch => {
  dispatch(getDataItemDetailsRequest());
  return DataItemAPI.getDataItemDetailsAPI(hierarchyPath, dataItem)
    .then(responseJson => {
      dispatch(getDataItemDetailsSuccess(responseJson || ''));
      return responseJson;
    })
    .catch(error => {
      dispatch(getDataItemDetailsFailure(error));
    });
};

// ---------------
// DataItem Graph Actions
// ---------------

const getDataItemGraph = () => ({
  type: GET_DATAITEM_GRAPH,
});

const getDataItemGraphSuccess = (dataItemName, dataItemGraphData) => ({
  type: GET_DATAITEM_GRAPH_SUCCESS,
  dataItemName,
  payload: dataItemGraphData,
});

const getDataItemGraphError = error => ({
  type: GET_DATAITEM_GRAPH_FAILURE,
  payload: error,
});

export const fetchDataItemGraph =
  (hierarchyPath, dataItem, selectedTimeRange, hierarchyItemType) => dispatch => {
    dispatch(getDataItemGraph());
    return DataItemAPI.getDataItemGraph(
      hierarchyPath,
      dataItem,
      selectedTimeRange,
      hierarchyItemType
    )
      .then(responseJson => {
        dispatch(getDataItemGraphSuccess(dataItem.name, responseJson.data));
        return responseJson.data;
      })
      .catch(error => {
        dispatch(getDataItemGraphError(error));
      });
  };

// ---------------
// DataItem Recent Data Actions
// ---------------

const getDataItemRecentData = () => ({
  type: GET_DATAITEM_RECENTDATA,
});

const getDataItemRecentDataSuccess = (dataItemName, dataItemRecentData) => ({
  type: GET_DATAITEM_RECENTDATA_SUCCESS,
  dataItemName,
  payload: dataItemRecentData,
});

const getDataItemRecentDataError = error => ({
  type: GET_DATAITEM_RECENTDATA_FAILURE,
  payload: error,
});

export const filterRecentDataResources = (recentData, deviceTypeUUId, currentUserResources) => {
  if (!isEmpty(currentUserResources) && recentData && !isEmpty(recentData)) {
    return recentData.filter(
      data =>
        data?.id && currentUserResources[RESOURCE_TYPES.DEVICE]?.[deviceTypeUUId]?.includes(data.id)
    );
  }
  return recentData ?? [];
};

export const fetchDataItemRecentData =
  (hierarchyPath, dataItem, resourceUUId, hierarchyItemType) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(getDataItemRecentData());
    return DataItemAPI.getDataItemRecentData(hierarchyPath, dataItem)
      .then(({ data }) => {
        const currentUserResources = dataCache.get('currentUserResources');
        if (hierarchyItemType === RESOURCE_TYPES.DEVICE_TYPE && !isEmpty(currentUserResources)) {
          // filter the data if its device type
          const filteredData = filterRecentDataResources(data, resourceUUId, currentUserResources);
          dispatch(getDataItemRecentDataSuccess(dataItem?.name, filteredData));
          return filteredData;
        }

        dispatch(getDataItemRecentDataSuccess(dataItem?.name, data));
        return data;
      })
      .catch(error => {
        dispatch(getDataItemRecentDataError(error));
      });
  };
